/**
 * Design Verification Items List Page
 *
 * Main page for managing 設計検証項目一覧 sessions.
 * Split layout: session list (left) + detail view (right).
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ListChecks } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import FeatureDisabled from '@components/common/FeatureDisabled';
import {
  fetchVerificationStatus,
  selectDesignVerificationEnabled,
} from '@features/featureFlags/featureFlagsSlice';
import { useAppDispatch, useAppSelector } from '@store/index';
import { resolveApiErrorMessage } from '@utils/apiErrorMessages';

import VerificationSessionList from '../components/verification/VerificationSessionList';
import VerificationItemEditor, {
  ItemFormData,
} from '../components/verification/VerificationItemEditor';
import VerificationExportDialog from '../components/verification/VerificationExportDialog';
import CreateSessionDialog, {
  CreateSessionForm,
} from '../components/verification/verification-page/CreateSessionDialog';
import DeleteConfirmDialog, {
  DeleteTarget,
} from '../components/verification/verification-page/DeleteConfirmDialog';
import UnitDialog from '../components/verification/verification-page/UnitDialog';
import VerificationPageHeader from '../components/verification/verification-page/VerificationPageHeader';
import VerificationSessionDetail from '../components/verification/verification-page/VerificationSessionDetail';
import VerificationSnackbar from '../components/verification/verification-page/VerificationSnackbar';
import { buildItemPayload } from '../components/verification/verification-page/itemPayload';
import { useUnitForm } from '../components/verification/verification-page/useUnitForm';
import { useVerificationChat } from '../components/verification/verification-page/useVerificationChat';
import { useVerificationSessions } from '../components/verification/verification-page/useVerificationSessions';
import { VerificationSession, VerificationItem } from '../types/verification';
import * as verificationService from '../services/verificationService';

const VerificationPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const designVerificationEnabled = useAppSelector(selectDesignVerificationEnabled);
  const handleUnavailable = React.useCallback(
    () => { dispatch(fetchVerificationStatus()); },
    [dispatch],
  );

  // Notifications
  const [snackMessage, setSnackMessage] = useState<string | null>(null);

  const {
    sessions,
    sessionsLoading,
    sessionsError,
    selectedSession,
    setSelectedSession,
    detailLoading,
    loadSessions,
    loadSessionDetail,
    prepareExport,
  } = useVerificationSessions(setSnackMessage, {
    // `=== true`: on a direct navigation the flag starts null, and fetching then
    // is a request the gate 404s. The page spins until it resolves.
    enabled: designVerificationEnabled === true,
    onUnavailable: handleUnavailable,
  });

  const chat = useVerificationChat(selectedSession);

  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [itemEditorOpen, setItemEditorOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);

  const unitForm = useUnitForm({
    selectedSession,
    onSaved: loadSessionDetail,
    onNotify: setSnackMessage,
  });

  // Form state
  const [deletePrepareId, setDeletePrepareId] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<VerificationItem | null>(null);
  const [targetUnitId, setTargetUnitId] = useState<string>('');

  // Session operations

  const handleSessionSelect = (session: VerificationSession) => {
    loadSessionDetail(session.id);
  };

  // The dialog validates against sessionSchema and hands over trimmed values, so
  // the only normalisation left here is empty-string -> undefined: `model_name`
  // and `description` are nullable columns, and '' would store a blank rather
  // than leave them unset.
  const handleCreateSession = async (sessionForm: CreateSessionForm) => {
    try {
      const created = await verificationService.createSession({
        title: sessionForm.title,
        modelName: sessionForm.modelName || undefined,
        description: sessionForm.description || undefined,
      });
      setCreateDialogOpen(false);
      await loadSessions();
      loadSessionDetail(created.id);
      setSnackMessage(t('verification.sessionCreated'));
    } catch (err) {
      setSnackMessage(resolveApiErrorMessage(t, err, { fallbackKey: 'verification.createSessionFailed' }));
    }
  };

  const handleDeleteSession = async (sessionId: string) => {
    // The detail fetch below is a network round trip; without this guard a slow
    // response leaves the control looking dead and a second click fires again.
    if (deletePrepareId) return;
    setDeletePrepareId(sessionId);

    try {
      // The list endpoint returns `units: []`, so the cascade counts the
      // warning quotes have to come from the detail payload.
      const detail =
        selectedSession?.id === sessionId
          ? selectedSession
          : await verificationService.getSession(sessionId).catch(() => null);
      const units = detail?.units ?? [];

      setDeleteTarget({
        id: sessionId,
        type: 'session',
        name: detail?.title ?? sessions.find((s) => s.id === sessionId)?.title ?? '',
        unitCount: units.length,
        itemCount: units.reduce((total, unit) => total + unit.items.length, 0),
      });
    } finally {
      setDeletePrepareId(null);
    }
  };

  const handleExportSession = async (sessionId: string) => {
    if (await prepareExport(sessionId)) setExportDialogOpen(true);
  };

  // Unit operations

  const handleDeleteUnit = (unitId: string) => {
    const unit = selectedSession?.units.find((u) => u.id === unitId);
    setDeleteTarget({
      id: unitId,
      type: 'unit',
      name: unit?.unitName ?? '',
      unitCount: 0,
      itemCount: unit?.items.length ?? 0,
    });
  };

  // Item operations

  const handleAddItem = (unitId: string) => {
    setEditingItem(null);
    setTargetUnitId(unitId);
    setItemEditorOpen(true);
  };

  const handleEditItem = (item: VerificationItem) => {
    setEditingItem(item);
    setTargetUnitId(item.unitId);
    setItemEditorOpen(true);
  };

  const handleSaveItem = async (data: ItemFormData) => {
    if (!selectedSession) return;
    try {
      if (editingItem) {
        await verificationService.updateItem(
          selectedSession.id,
          editingItem.id,
          buildItemPayload(data),
        );
      } else {
        await verificationService.addItem(selectedSession.id, {
          unitId: targetUnitId,
          ...buildItemPayload(data),
        });
      }
      setItemEditorOpen(false);
      loadSessionDetail(selectedSession.id);
      setSnackMessage(editingItem ? t('success.updated') : t('verification.itemAdded'));
    } catch (err) {
      setSnackMessage(resolveApiErrorMessage(t, err, { fallbackKey: 'verification.saveItemFailed' }));
    }
  };

  const handleDeleteItem = (itemId: string) => {
    const item = selectedSession?.units
      .flatMap((unit) => unit.items)
      .find((candidate) => candidate.id === itemId);
    setDeleteTarget({
      id: itemId,
      type: 'item',
      name: item?.itemName ?? '',
      unitCount: 0,
      itemCount: 0,
    });
  };

  // Delete confirm

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      if (deleteTarget.type === 'session') {
        await verificationService.deleteSession(deleteTarget.id);
        setSelectedSession(null);
        await loadSessions();
      } else if (deleteTarget.type === 'unit') {
        if (!selectedSession) return;
        await verificationService.deleteUnit(selectedSession.id, deleteTarget.id);
        loadSessionDetail(selectedSession.id);
      } else if (deleteTarget.type === 'item') {
        if (!selectedSession) return;
        await verificationService.deleteItem(selectedSession.id, deleteTarget.id);
        loadSessionDetail(selectedSession.id);
      }
      setDeleteTarget(null);
      setSnackMessage(t('success.deleted'));
    } catch (err) {
      setSnackMessage(resolveApiErrorMessage(t, err, { fallbackKey: 'verification.deleteFailed' }));
    }
  };

  if (designVerificationEnabled === null) {
    return (
      <div className="flex h-[calc(100vh-64px)] items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (designVerificationEnabled === false) {
    return (
      <FeatureDisabled
        icon={<ListChecks />}
        title={t('verification.disabledTitle')}
        description={t('verification.disabled')}
      />
    );
  }

  return (
    <div className="flex h-full flex-col">
      <VerificationPageHeader
        chatActive={chat.active}
        onToggleChat={chat.toggle}
        showExport={!!selectedSession}
        onExport={() => setExportDialogOpen(true)}
        onBack={() => navigate('/dashboard')}
      />

      {/* Main content: split layout */}
      <div className="flex flex-1 gap-4 overflow-hidden px-4 pb-4">
        <div className="w-60 shrink-0 overflow-auto">
          <VerificationSessionList
            sessions={sessions}
            selectedSessionId={selectedSession?.id || null}
            isLoading={sessionsLoading}
            error={sessionsError}
            onSessionSelect={handleSessionSelect}
            onSessionDelete={handleDeleteSession}
            onSessionExport={handleExportSession}
            onRefresh={loadSessions}
            onCreate={() => setCreateDialogOpen(true)}
          />
        </div>

        <div className="min-w-0 flex-1 overflow-auto">
          <VerificationSessionDetail
            isLoading={detailLoading}
            session={selectedSession}
            onAddUnit={unitForm.startAdd}
            onEditUnit={unitForm.startEdit}
            onDeleteUnit={handleDeleteUnit}
            onAddItem={handleAddItem}
            onEditItem={handleEditItem}
            onDeleteItem={handleDeleteItem}
          />
        </div>
      </div>

      <CreateSessionDialog
        open={createDialogOpen}
        onSubmit={handleCreateSession}
        onClose={() => setCreateDialogOpen(false)}
      />

      <UnitDialog
        open={unitForm.open}
        isEditing={unitForm.isEditing}
        isDirty={unitForm.isDirty}
        form={unitForm.form}
        onSubmit={unitForm.save}
        onClose={unitForm.close}
      />

      <VerificationItemEditor
        open={itemEditorOpen}
        item={editingItem}
        unitId={targetUnitId}
        onSave={handleSaveItem}
        onClose={() => setItemEditorOpen(false)}
      />

      <VerificationExportDialog
        open={exportDialogOpen}
        session={selectedSession}
        onClose={() => setExportDialogOpen(false)}
      />

      <DeleteConfirmDialog
        target={deleteTarget}
        onConfirm={handleConfirmDelete}
        onClose={() => setDeleteTarget(null)}
      />

      <VerificationSnackbar message={snackMessage} onClose={() => setSnackMessage(null)} />
    </div>
  );
};

export default VerificationPage;
